import { Component, Input, OnInit, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormBuilder, FormArray, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { DialogModule } from 'primeng/dialog';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { OnboardingService } from '../../../../services/onboarding/onboarding.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { BlockUIModule } from 'primeng/blockui';
import { validateFile } from '../../../../direct-apply/shared/validators/form-validation.utils';


export interface UploadedFileItem {
  file?: File;
  previewUrl: string | SafeResourceUrl;
  name: string;
  type?: string;
  base64Data?: string;
}

@Component({
  selector: 'app-recommendations',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    FileUploadModule,
    ButtonModule,
    DividerModule,
    DialogModule,
    ToastModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    BlockUIModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './recommendations.component.html',
  styleUrls: ['./recommendations.component.scss']
})
export class RecommendationsComponent implements OnInit {
  @Input() parentForm!: FormGroup;
  @Output() stepSaved = new EventEmitter<void>();
  @Output() saveFailed = new EventEmitter<void>();
  @Output() stepStatusChanged = new EventEmitter<boolean>();

  recommendationsForm!: FormArray;

  @ViewChild('hiddenFileInput') hiddenFileInput!: ElementRef<HTMLInputElement>;
  currentUploadIndex: number = -1;
  displayPreviewDialog: boolean = false;
  selectedPreviewItem: UploadedFileItem | null = null;
  safePdfUrl: SafeResourceUrl | null = null;

  studentId: string = '';
  savingInProgress: boolean = false;
  existingIds: (number | null)[] = [];

  savingStates: { saving: boolean }[] = [];
  isLoading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private onboardingService: OnboardingService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit() {
    this.isLoading = true;
    this.recommendationsForm = this.fb.array([]);
    this.parentForm.setControl('recommendations', this.recommendationsForm);
    this.recommendationsForm.valueChanges.subscribe(() => this.checkStepStatus());

    const storedData = localStorage.getItem('account');
    if (storedData) {
      const account = JSON.parse(storedData);
      this.studentId = account.userId || account.id || account.Id || account.sub || '';
      if (this.studentId) {
        this.loadExistingData();
      } else {
        this.isLoading = false;
      }
    } else {
      this.isLoading = false;
    }
  }


  loadExistingData() {
    this.isLoading = true;
    this.onboardingService.getRecommendations().subscribe({
      next: (res: any) => {
        const entries = Array.isArray(res) ? res : (res?.result || []);
        if (entries.length > 0) {
          entries.forEach((entry: any) => {
            const id = entry.id || entry.Id;
            this.existingIds.push(id || null);
            this.savingStates.push({ saving: false });

            const group = this.createRecommendation();
            group.patchValue({
              name: entry.name || '',
              organisation: entry.organisationName || '',
              email: entry.officialEmail || '',
              contact: entry.contactNumber || ''
            });

            const docUrl = entry.lorUrl;
            if (docUrl) {
              const urlWithoutParams = docUrl.split('?')[0];
              const fileName = urlWithoutParams.split('/').pop() || 'document';
              const type = urlWithoutParams.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg';
              group.get('lorFiles')?.setValue([{ name: fileName, previewUrl: docUrl, type, base64Data: docUrl }]);
              group.get('lorFiles')?.setErrors(null);
            }

            this.recommendationsForm.push(group);
          });
        }

        while (this.recommendationsForm.length < 1) {
          this.addRecommendation();
        }
        this.isLoading = false;
        this.checkStepStatus();
      },
      error: (err) => {
        console.error('Failed to load recommendations', err);
        while (this.recommendationsForm.length < 1) this.addRecommendation();
        this.isLoading = false;
        this.checkStepStatus();
      }
    });
  }

  checkStepStatus() {
    let allSaved = true;
    for (let i = 0; i < this.recommendationsForm.length; i++) {
      const isSaved = this.existingIds[i] !== null;
      const isDirty = this.recommendationsForm.at(i).dirty;
      // Block if: has unsaved changes on existing record, OR is new and not pristine
      if (isDirty || (!isSaved && !this.recommendationsForm.at(i).pristine)) {
        allSaved = false;
      }
    }
    // Also if there's no form, it's technically fine since it's optional.
    this.stepStatusChanged.emit(allSaved);
  }

  createRecommendation(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
      organisation: ['', [Validators.required, Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
      email: ['', [Validators.required, Validators.email]],
      contact: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      lorFiles: [[]]
    });
  }

  addRecommendation() {
    this.recommendationsForm.push(this.createRecommendation());
    this.existingIds.push(null);
    this.savingStates.push({ saving: false });
    this.checkStepStatus();
  }

  removeRecommendation(index: number) {
    const id = this.existingIds[index];

    const doRemoveInUI = () => {
      this.recommendationsForm.removeAt(index);
      this.existingIds.splice(index, 1);
      this.savingStates.splice(index, 1);
      this.checkStepStatus();
    };

    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this recommendation record?',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptButtonStyleClass: 'p-button-primary custom-confirm-yes',
      rejectButtonStyleClass: 'p-button-primary custom-confirm-no',
      accept: () => {
        if (id) {
          this.onboardingService.deleteRecommendation(id).subscribe({
            next: () => {
              this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Recommendation record removed.' });
              doRemoveInUI();
            },
            error: () => {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete record from server.' });
            }
          });
        } else {
          doRemoveInUI();
        }
      }
    });
  }

  onFileUpload(event: any, index: number, uploader?: any) {
    if (event.files && event.files.length > 0) {
      for (let file of event.files) {
        const check = validateFile(file);
        if (!check.valid) {
          this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: check.message });
          continue;
        }
        this.processFile(file, index);
      }
    }
    if (uploader) {
      uploader.clear();
    }
  }

  processFile(file: File, index: number) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const previewUrl = file.type === 'application/pdf'
        ? this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file))
        : e.target.result;
      const newItem: UploadedFileItem = { file, previewUrl, name: file.name, type: file.type, base64Data: e.target.result };

      const control = this.recommendationsForm.at(index)?.get('lorFiles');
      if (control) {
        control.setValue([newItem]);
        control.markAsDirty();
        control.setErrors(null);
      }
    };
    reader.readAsDataURL(file);
  }

  triggerAddMore(index: number) {
    this.currentUploadIndex = index;
    if (this.hiddenFileInput) {
      this.hiddenFileInput.nativeElement.click();
    }
  }

  onHiddenFileChange(event: any) {
    const files = event.target.files;
    if (files && files.length > 0 && this.currentUploadIndex >= 0) {
      Array.from(files).forEach((file: any) => {
        this.processFile(file as File, this.currentUploadIndex);
      });
    }
    event.target.value = '';
  }

  removeFileObj(recIndex: number) {
    const control = this.recommendationsForm.at(recIndex)?.get('lorFiles');
    if (control) {
      control.setValue([]);
      control.markAsTouched();
      control.markAsDirty();
      control.updateValueAndValidity();
    }
  }

  getUploadedFiles(index: number): UploadedFileItem[] {
    const val = this.recommendationsForm.at(index)?.get('lorFiles')?.value;
    return Array.isArray(val) ? val : [];
  }

  getDocumentDisplayName(index: number): string {
    // e.g. "1st Recommendation Letter"
    return `${this.getOrdinal(index + 1)} Recommendation Letter`;
  }

  hasFiles(index: number): boolean {
    return this.getUploadedFiles(index).length > 0;
  }

  isPreviewLoading: boolean = false;

  onPreviewLoad() {
    this.isPreviewLoading = false;
  }

  openPreview(item: UploadedFileItem) {
    this.selectedPreviewItem = item;
    this.isPreviewLoading = true;
    this.safePdfUrl = null;

    if (item.type === 'application/pdf') {
      const url = item.previewUrl;
      if (typeof url === 'string') {
        if (url.startsWith('http')) {
          const urlStr = encodeURIComponent(url);
          this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`https://docs.google.com/viewer?url=${urlStr}&embedded=true`);
        } else {
          this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        }
      } else {
        this.safePdfUrl = url;
      }
    } else if (item?.type?.startsWith('image/')) {
    } else {
      this.isPreviewLoading = false;
    }
    this.displayPreviewDialog = true;
  }

  extractDocUrl(controlValue: any[]): string | null {
    if (!controlValue || controlValue.length === 0) return null;
    const item = controlValue[0];
    return (typeof item.previewUrl === 'string' ? item.previewUrl : null) || item.base64Data || null;
  }

  saveEntry(index: number) {
    void this.persistEntry(index);
  }

  private persistEntry(index: number): Promise<boolean> {
    const group = this.recommendationsForm.at(index);
    if (!group) return Promise.resolve(false);
    const existingId = this.existingIds[index];
    if (existingId && !group.dirty) {
      this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
      return Promise.resolve(true);
    }

    group.markAllAsTouched();
    if (group.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.', life: 5000 });
      return Promise.resolve(false);
    }
    if (this.savingStates[index]) this.savingStates[index].saving = true;
    this.isLoading = true;

    const vals = group.value;


    const fd = new FormData();
    fd.append('Name', vals.name || '');
    fd.append('OrganisationName', vals.organisation || '');
    fd.append('OfficialEmail', vals.email || '');
    fd.append('ContactNumber', vals.contact || '');

    const files: any[] = Array.isArray(vals.lorFiles) ? vals.lorFiles : [];
    if (files.length === 0) {
      fd.append('RemoveDocument', 'true');
    } else if (files.length > 0 && files[0].file instanceof File) {
      fd.append('LORUrl', files[0].file, files[0].name);
    }

    if (existingId) {
      fd.append('Id', existingId.toString());
      return new Promise((resolve) => this.onboardingService.updateRecommendation(existingId, fd).subscribe({
        next: (res: any) => {
          const newId = res?.id || res?.Id || res?.result?.id || res?.result?.Id;
          if (newId) this.existingIds[index] = newId;
          group.markAsPristine();
          if (this.savingStates[index]) this.savingStates[index].saving = false;
          this.isLoading = false;
          this.checkStepStatus();
          resolve(true);
        },
        error: (err) => {
          console.error('Update failed', err);
          if (this.savingStates[index]) this.savingStates[index].saving = false;
          this.isLoading = false;
          resolve(false);
        }
      }));
    } else {
      return new Promise((resolve) => this.onboardingService.createRecommendation(fd).subscribe({
        next: (res: any) => {
          const newId = res?.id || res?.Id || res?.result?.id || res?.result?.Id;
          if (newId) this.existingIds[index] = newId;
          group.markAsPristine();
          if (this.savingStates[index]) this.savingStates[index].saving = false;
          this.isLoading = false;
          this.checkStepStatus();
          resolve(true);
        },
        error: (err) => {
          console.error('Create failed', err);
          if (this.savingStates[index]) this.savingStates[index].saving = false;
          this.isLoading = false;
          resolve(false);
        }
      }));
    }
  }

  async saveData(): Promise<void> {
    if (this.savingInProgress) return;

    for (let i = this.recommendationsForm.length - 1; i >= 0; i--) {
      const group = this.recommendationsForm.at(i);
      const isUnsaved = !this.existingIds[i];
      const vals = group.value;
      const isEmpty = !vals.name && !vals.organisation && !vals.email && !vals.contact && (!vals.lorFiles || vals.lorFiles.length === 0);
      
      if (isUnsaved && isEmpty) {
        this.recommendationsForm.removeAt(i);
        this.existingIds.splice(i, 1);
        this.savingStates.splice(i, 1);
      }
    }

    if (this.recommendationsForm.length > 0 && !this.recommendationsForm.valid) {
      this.recommendationsForm.markAllAsTouched();
      this.saveFailed.emit();
      return;
    }

    this.savingInProgress = true;
    const results = await Promise.all(this.recommendationsForm.controls.map((_, index) => this.persistEntry(index)));
    this.savingInProgress = false;

    if (this.recommendationsForm.length === 0) {
      this.addRecommendation();
    }

    if (results.every(Boolean)) {
      this.stepSaved.emit();
    } else {
      this.saveFailed.emit();
    }
  }

  getOrdinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }
}